import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useInventoryTransfer } from '../../../../../hooks/inventory/useInventoryTransfer';
import { useInventoryTransferDetail } from '../../../../../hooks/inventory/useInventoryTransferDetail';
import { useBranch } from '../../../../../hooks/branches/useBranch';
import { useAuthUser } from '../../../../../hooks/auth/useAuthUser';
import { validateInventoryTransferForm } from '../../../../../features/inventory/transfer/validateInventoryTransferForm';
import FormModal from '../../../../Modals/FormModal';
import SelectField from '../../../../Forms/Input/SelectField';
import TextAreaField from '../../../../Forms/Input/TextAreaField';
import ActionButton from '../../../../Buttons/ActionButton';
import DesktopTableInlineActions from '../../../../Tables/desktop/DesktopTableInlineActions';
import { buildTransferProductColumns } from './columns/transferProductColumns';
import ProductStockPickerTable from '../../../shared/ProductStockPickerTable';
import { InventoryTransferDetailLineDTO } from '../../../../../types/inventory/transfer/InventoryTransferTypes';
import { ProductStockLookupDTO } from '../../../../../types/inventory/stock/ProductStockLookupTypes';

export const CreateInventoryTransferModal = () => {
  const {
    form,
    formErrors,
    isSubmitting,
    showCreateModal,
    closeCreateModal,
    updateField,
    setErrors,
    handlerCreate,
  } = useInventoryTransfer();

  const { addDetail, updateDetail, deleteDetailById } = useInventoryTransferDetail();
  const { branchDropdown, fetchBranchDropdown, myBranch, fetchMyBranch } = useBranch();
  const { hasRole } = useAuthUser();

  const [showProductPicker, setShowProductPicker] = useState(false);

  const hasTransfer = !!form.id;
  const isEditable = form.status === 'REQUESTED' || !hasTransfer;

  // Solo estos roles pueden trasladar DESDE una sucursal distinta a la
  // propia; para el resto el origen se toma siempre de la del usuario, sin
  // mostrar el selector. El destino sigue siendo una elección explícita de
  // todos (esa es la operación).
  const canPickOriginBranch = hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  // cargar sucursales al abrir el modal
  React.useEffect(() => {
    if (showCreateModal) {
      fetchBranchDropdown();
      fetchMyBranch();
    }
  }, [showCreateModal]);

  // Precarga el origen con la sucursal del usuario (editable si tiene el rol).
  React.useEffect(() => {
    if (showCreateModal && myBranch && !form.originBranchId) {
      updateField('originBranchId', myBranch.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, myBranch]);

  const handleSave = async () => {
    const errors = validateInventoryTransferForm({
      originBranchId: form.originBranchId ? Number(form.originBranchId) : undefined,
      destinationBranchId: Number(form.destinationBranchId),
      reason: form.reason?.trim() || '',
    });

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    await handlerCreate({
      originBranchId: form.originBranchId ? Number(form.originBranchId) : undefined,
      destinationBranchId: Number(form.destinationBranchId),
      reason: form.reason || '',
    });
  };

  const handleAddProduct = () => {
    if (!hasTransfer || !isEditable) return;
    setShowProductPicker(true);
  };

  // 🛑 El selector se queda ABIERTO tras elegir un producto — así se pueden
  // agregar varios seguidos sin tener que reabrirlo (y volver a pedir la
  // lista) cada vez. El producto ya agregado desaparece solo de la lista
  // porque se excluye por excludeProductIds.
  const handlePickProduct = async (product: ProductStockLookupDTO) => {
    await addDetail({ productId: product.productId, quantity: 1 });
  };

  const handleQuantityChange = async (detailId: number, quantity: number) => {
    await updateDetail(detailId, { quantity });
  };

  const handleDeleteProduct = async (row: InventoryTransferDetailLineDTO) => {
    if (!isEditable) return;
    await deleteDetailById(row.id);
  };

  const productActions = isEditable
    ? (row: InventoryTransferDetailLineDTO) => [
        {
          label: 'Eliminar',
          icon: 'trash',
          variant: 'danger',
          onClick: () => handleDeleteProduct(row),
        },
      ]
    : undefined;

  const productColumns = buildTransferProductColumns(handleQuantityChange);

  return (
    <>
      <FormModal
        isOpen={showCreateModal}
        onClose={closeCreateModal}
        title="Crear Traslado de Inventario"
        maxWidth="max-w-6xl"
        zIndex="front"
      >
        {/* ---------------------------- */}
        {/* 🧱 Selección de sucursales   */}
        {/* ---------------------------- */}
        <div className={`mb-6 grid grid-cols-1 gap-4 ${canPickOriginBranch ? 'sm:grid-cols-2' : ''}`}>
          {canPickOriginBranch && (
            <SelectField
              label="Sucursal Origen"
              name="originBranchId"
              iconKey="store"
              value={form.originBranchId}
              onChange={(e) => updateField('originBranchId', Number(e.target.value))}
              options={branchDropdown.map((b) => ({
                value: b.id,
                label: b.name,
              }))}
              error={formErrors.originBranchId}
              disabled={hasTransfer}
              required
            />
          )}

          <SelectField
            label="Sucursal Destino"
            name="destinationBranchId"
            iconKey="store"
            value={form.destinationBranchId}
            onChange={(e) => updateField('destinationBranchId', Number(e.target.value))}
            options={branchDropdown.map((b) => ({
              value: b.id,
              label: b.name,
            }))}
            error={formErrors.destinationBranchId}
            disabled={hasTransfer}
            required
          />
        </div>

        {/* ---------------------------- */}
        {/* 📝 Razón del traslado        */}
        {/* ---------------------------- */}
        <div className="mb-6">
          <TextAreaField
            label="Razón"
            name="reason"
            iconKey="edit"
            value={form.reason || ''}
            onChange={(e) => updateField('reason', e.target.value)}
            placeholder="Ej: Traslado por reorganización"
            rows={3}
            disabled={hasTransfer}
          />
        </div>

        {/* ---------------------------- */}
        {/* 📦 Productos (solo tras crear la solicitud) */}
        {/* ---------------------------- */}
        {hasTransfer && (
          <div className="mb-6">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold">Productos a Trasladar</h3>

              {!showProductPicker && (
                <ActionButton
                  label="Agregar Producto"
                  icon={<Plus className="h-4 w-4" />}
                  size="sm"
                  fullWidth={false}
                  disabled={!isEditable}
                  onClick={handleAddProduct}
                />
              )}
            </div>

            {/* ---------------------------- */}
            {/* Selector paginado de producto (sin modal) */}
            {/* ---------------------------- */}
            {showProductPicker && (
              <div className="mb-4">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Elegir producto</h4>
                  <ActionButton
                    label="Cerrar"
                    variant="secondary"
                    size="sm"
                    fullWidth={false}
                    onClick={() => setShowProductPicker(false)}
                  />
                </div>
                <ProductStockPickerTable
                  branchId={form.originBranchId}
                  onSelect={handlePickProduct}
                  excludeProductIds={form.details.map((d) => d.productId)}
                />
              </div>
            )}

            <DesktopTableInlineActions
              columns={productColumns}
              data={form.details}
              actions={productActions}
              noDataMessage="No hay productos agregados."
            />
          </div>
        )}

        {/* ---------------------------- */}
        {/* 🔘 Botones                   */}
        {/* ---------------------------- */}
        <div className="mt-8 flex justify-end gap-3">
          {!hasTransfer ? (
            <ActionButton
              label="Crear Solicitud"
              fullWidth={false}
              loading={isSubmitting}
              loadingLabel="Creando..."
              onClick={handleSave}
            />
          ) : (
            <ActionButton
              label="Cerrar"
              variant="secondary"
              fullWidth={false}
              onClick={closeCreateModal}
            />
          )}
        </div>
      </FormModal>
    </>
  );
};
